import { useCallback } from 'react'
import { ReferencedEntityWithIcon } from '../../../../domain/value-object/ReferencedEntity'
import { useWbsItemRepository } from '../../../../services/adaptors/wbsItemRepositoryAdaptor'
import {
  ROOT_WBS_ITEM_TYPES,
  ROOT_WBS_ITEM_TYPES_WITH_DELIVERABLE,
} from '../wbsItemSearch'

const CACHE_TTL_MS = 60_000

type CacheEntry = {
  promise: Promise<ReferencedEntityWithIcon[]>
  expiresAt: number
}

// 検索条件ダイアログの初回オープン・保存条件適用・画面初期化時の復元処理など、
// 同一プロジェクトに対して短時間に複数経路から呼ばれるWBS全件取得APIをまとめるための
// プロセス共有キャッシュ。in-flight Promise をそのまま再利用するため、並行リクエストも
// 1回にまとまる。失敗したエントリは破棄して次回の再試行を可能にする。
const cache = new Map<string, CacheEntry>()

// 期限切れのエントリは、そのキーが次に読まれるまでMapに残り続けてしまうため、
// 呼び出しのたびに掃除する（キーがモードごとに複数できるようになったため）
const evictExpiredEntries = (now: number): void => {
  for (const [key, entry] of cache) {
    if (entry.expiresAt <= now) {
      cache.delete(key)
    }
  }
}

export const useRootWbsItems = (
  projectUuid: string,
  includeDeliverables: boolean = false
): (() => Promise<ReferencedEntityWithIcon[]>) => {
  const { fetchListAsReferencedEntity } = useWbsItemRepository()
  const types = includeDeliverables
    ? ROOT_WBS_ITEM_TYPES_WITH_DELIVERABLE
    : ROOT_WBS_ITEM_TYPES
  const cacheKey = `${projectUuid}:${includeDeliverables}`

  return useCallback(() => {
    const now = Date.now()
    evictExpiredEntries(now)
    const cached = cache.get(cacheKey)
    if (cached && cached.expiresAt > now) {
      return cached.promise
    }
    const promise = fetchListAsReferencedEntity(projectUuid, types).catch(
      error => {
        if (cache.get(cacheKey)?.promise === promise) {
          cache.delete(cacheKey)
        }
        throw error
      }
    )
    cache.set(cacheKey, {
      promise,
      expiresAt: now + CACHE_TTL_MS,
    })
    return promise
  }, [cacheKey, projectUuid, types, fetchListAsReferencedEntity])
}
